import { useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { FabricImage } from "@/components/FabricImage";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const ImageLightbox = ({ items, index, onIndexChange, onClose }) => {
  const open = index !== null && index >= 0 && !!items[index];
  const item = open ? items[index] : null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") onIndexChange((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndexChange((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, items, onIndexChange]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="image-lightbox"
        className="max-w-4xl gap-0 rounded-sm border-zinc-950 bg-white p-0"
      >
        <DialogTitle className="sr-only">Shade photo</DialogTitle>
        <DialogDescription className="sr-only">Large preview of the selected shade photo</DialogDescription>
        {item && (
          <>
            <div className="relative flex items-center justify-center bg-zinc-950">
              <FabricImage
                path={item.image_path}
                alt={`${item.quality_name} shade ${item.shade_number}`}
                className="max-h-[70vh] w-full object-contain"
                w={1200}
              />
              {items.length > 1 && (
                <>
                  <button
                    data-testid="lightbox-prev"
                    onClick={() => onIndexChange((index - 1 + items.length) % items.length)}
                    className="absolute left-2 flex h-10 w-10 items-center justify-center border border-white/30 bg-black/50 text-white transition-colors hover:bg-black/80"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    data-testid="lightbox-next"
                    onClick={() => onIndexChange((index + 1) % items.length)}
                    className="absolute right-2 flex h-10 w-10 items-center justify-center border border-white/30 bg-black/50 text-white transition-colors hover:bg-black/80"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-col gap-3 border-t border-zinc-200 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-head text-lg font-bold tracking-tight" data-testid="lightbox-title">
                  {item.quality_name}
                  {item.shade_number ? ` · Shade ${item.shade_number}` : ""}
                </p>
                <p className="mt-0.5 text-xs uppercase tracking-[0.15em] text-zinc-500">
                  {[item.colour, item.company_name, item.centre].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <div className="flex gap-6">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">Than</p>
                  <p className="font-mono-num text-sm font-semibold">{item.than_stock ?? 0}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">Meter</p>
                  <p className="font-mono-num text-sm font-semibold">{item.meter_stock ?? 0}</p>
                </div>
                {item.wsr ? (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">WSR</p>
                    <p className="font-mono-num text-sm font-semibold">₹{item.wsr}</p>
                  </div>
                ) : null}
                <p className="self-end font-mono-num text-xs text-zinc-400">
                  {index + 1}/{items.length}
                </p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
